import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetTrialBalanceResponse } from '@/api/handlers/resources/trialBalance/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { TrialBalanceQueryKeys } from './trialBalanceQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * The trial balance as of a date.
 *
 * Keyed on `asOf`, `locale` and `excludeClosing`, so changing any of them
 * refetches; `keepPreviousData` holds the previous report on screen while the
 * new one loads, so changing the date does not flash back to a skeleton.
 *
 * That placeholder is safe here **because a stale report is never mistaken for a
 * fresh one**: the page's date control shows what was asked for and the report
 * re-renders coherently when the answer lands. What must never happen — a page
 * of zeros that balances perfectly while the ledger is unreachable — is handled
 * on the other side: a failed read has no data at all, and the page throws to
 * its `ErrorBoundary` rather than rendering an empty trial balance.
 */
export const useTrialBalanceQuery = (
    asOf: string,
    locale: string,
    excludeClosing: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetTrialBalanceResponse>>({
        queryKey: TrialBalanceQueryKeys.report(asOf, locale, excludeClosing),
        queryFn: () =>
            apiClient.trialBalance.getTrialBalance(asOf, {
                locale,
                excludeClosing,
            }),
        enabled: asOf.length > 0,
        staleTime: STALE_TIME_MS,
        placeholderData: keepPreviousData,
    });
};
